import React, { useReducer, useEffect } from 'react';
import createNetworkingClient from 'utils/networking-client';
import AdyenForm from '../AdyenForm';
import { useCheckoutContext } from '../../state/CheckoutContext';

const INITIALIZED = 'initialized';
const UNSET = 'unset';
const ACTION_SET_SESSION_SUCCESS = 'setSessionSuccess';
const ACTION_SET_SESSION_ERROR = 'setSessionError';
const generateSession = async (recurring, amount, currency, dispatch) => {
  const client = createNetworkingClient();
  const options = {
    method: 'POST',
    url: '/adyen/generate_session',
    data: { recurring }
  };
  client
    .request(options)
    .then((response) => {
      const payload = {
        sessionId: response.data.session_id,
        sessionData: response.data.session_data,
        amount,
        currency,
        recurring
      };
      dispatch({ type: ACTION_SET_SESSION_SUCCESS, payload });
    })
    .catch(() => {
      dispatch({ type: ACTION_SET_SESSION_ERROR, payload: { recurring } });
    });
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_SET_SESSION_SUCCESS:
      return {
        ...state,
        [action.payload.recurring]: {
          sessionData: action.payload.sessionData,
          sessionId: action.payload.sessionId,
          amount: action.payload.amount,
          status: INITIALIZED
        }
      };
    case ACTION_SET_SESSION_ERROR:
      return {
        ...state,
        [action.payload.recurring]: {
          sessionData: null,
          sessionId: null,
          amount: null,
          status: INITIALIZED
        }
      };
    default:
      throw new Error();
  }
};

const initialSessionData = (
  sessionData,
  sessionId,
  duration,
  amount,
  currency
) => {
  const data = {
    currency,
    yearly: {
      amount: null,
      status: UNSET,
      sessionData: '',
      sessionId: ''
    },
    monthly: {
      amount: null,
      status: UNSET,
      sessionData: '',
      sessionId: ''
    }
  };

  data[duration].sessionId = sessionId;
  data[duration].sessionData = sessionData;
  data[duration].status = INITIALIZED;
  data[duration].amount = amount;

  return data;
};

// AdyenSessionAdapter supports managing of internal state for fetching and minting
// new session data when the price changes. For the dropin, component we need
// to create a new session on the server that has the price and currency to use
// in concert with the dropin component.
const AdyenSessionAdapter = () => {
  const { checkoutEntityState } = useCheckoutContext();

  const {
    duration,
    sessionData,
    sessionId,
    clientKey,
    environment,
    products
  } = checkoutEntityState;
  const currency = products?.[duration]?.currency;
  const amount = products?.[duration]?.amountInMinorUnits;

  const [state, dispatch] = useReducer(
    reducer,
    initialSessionData(sessionData, sessionId, duration, amount, currency)
  );
  const currentStatus = state[duration].status;
  const currentSessionId = state[duration].sessionId;
  const currentSessionData = state[duration].sessionData;
  const currentCurrency = state.currency;
  const currentAmount = state[duration].amount;

  useEffect(() => {
    if (currentStatus === INITIALIZED) {
      return;
    }
    generateSession(duration, amount, currency, dispatch);
  }, [duration, currentStatus]);

  return (
    <AdyenForm
      sessionData={currentSessionData}
      sessionId={currentSessionId}
      clientKey={clientKey}
      environment={environment}
      currency={currentCurrency}
      amount={currentAmount}
    />
  );
};

export default AdyenSessionAdapter;
